import { useId, useState } from "react";

import { Button } from "@/web/components/primitives/button";
import { Checkbox } from "@/web/components/primitives/checkbox";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { RadioGroup, RadioGroupItem } from "@/web/components/primitives/radio-group";
import { Textarea } from "@/web/components/primitives/textarea";
import { cn } from "@/web/lib/styling";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import {
  ActionCard,
  useActionCardPalette,
} from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { CATEGORY_PALETTES } from "@/web/routes/_protected/smb/-dashboard/-categoryPalettes";
import { QuestionDetailsContent } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionDetailsContent";
import { QuestionSubtitleMarkdown } from "@/web/routes/_protected/smb/-dashboard/-questions/QuestionSubtitleMarkdown";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
  SmbQuestionDetails,
} from "@/web/routes/_protected/smb/-dashboard/-types";

const BUDGET_CARD_PALETTE = CATEGORY_PALETTES.budget;

export type MultipleChoiceQuestionProps = {
  questionId: string;
  badge: string;
  question: string;
  subtitle?: string;
  options: { id: string; label: string; position: string }[];
  allowMultiple?: boolean;
  details?: SmbQuestionDetails;
};

function OptionRow({
  label,
  value,
  checked,
  onCheckedChange,
  allowMultiple,
}: {
  label: string;
  value: string;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  allowMultiple: boolean;
}) {
  const id = useId();
  const palette = useActionCardPalette();

  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-center gap-3 text-left",
        checked ? "opacity-100" : "opacity-60",
      )}
    >
      {allowMultiple ? (
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className="size-5 text-withus-white"
          indicatorClassName={palette.cardBg}
        />
      ) : (
        <RadioGroupItem id={id} value={value} className="size-5 text-withus-white" />
      )}
      <span className="text-sm leading-snug">{label}</span>
    </label>
  );
}

function MultipleChoiceQuestion({
  questionId,
  badge,
  question,
  subtitle,
  options,
  allowMultiple = false,
  details,
  onSkip,
  onComplete,
}: MultipleChoiceQuestionProps & SmbActionCoreProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);

  const toggleOption = (optionId: string) => {
    if (allowMultiple) {
      setSelected((prev) =>
        prev.includes(optionId)
          ? prev.filter((existing) => existing !== optionId)
          : [...prev, optionId],
      );
      return;
    }

    setSelected([optionId]);
  };

  const { mutate: submitAnswer } = useProcedureMutation(
    procedures.submitUnderwritingQuestionAnswer,
  );

  const handleContinue = () => {
    // TODO(fas): error handling?
    submitAnswer({
      answer: { tag: "MultipleChoice", value: { value: selected, note: note.trim() || undefined } },
      questionId: questionId,
    });
    onComplete({ selected, note: note.trim() || undefined });
  };

  const canSubmit = selected.length > 0;

  return (
    <>
      <ActionCard category="budget">
        <ActionCard.Header>
          <ActionCard.Badge>{badge}</ActionCard.Badge>
          {details && <ActionCard.DetailsTrigger />}
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>{question}</ActionCard.Title>
          {subtitle && (
            <ActionCard.Description>
              <QuestionSubtitleMarkdown markdown={subtitle} />
            </ActionCard.Description>
          )}
        </ActionCard.Main>
        <ActionCard.Body className="pr-1">
          <div className="flex flex-col gap-4 pb-1">
            {allowMultiple ? (
              <div className="flex flex-col gap-3 py-3">
                {options.map((option) => (
                  <OptionRow
                    key={option.id}
                    label={option.label}
                    value={option.id}
                    checked={selected.includes(option.id)}
                    onCheckedChange={() => toggleOption(option.id)}
                    allowMultiple
                  />
                ))}
              </div>
            ) : (
              <RadioGroup
                value={selected[0] ?? ""}
                onValueChange={(value) => value && toggleOption(value)}
                className="flex flex-col gap-3 py-3"
              >
                {options.map((option) => (
                  <OptionRow
                    key={option.id}
                    label={option.label}
                    value={option.id}
                    checked={selected.includes(option.id)}
                    allowMultiple={false}
                  />
                ))}
              </RadioGroup>
            )}
          </div>
        </ActionCard.Body>

        <ActionCard.Footer>
          {!canSubmit ? (
            <ActionCard.SkipButton onSkip={onSkip} />
          ) : (
            <>
              <Button
                variant="outline"
                color={BUDGET_CARD_PALETTE.skipButtonColor}
                size="md"
                data-slot="smb-action-card-drawer-trigger"
                className={BUDGET_CARD_PALETTE.skipButtonClass}
                aria-expanded={noteDrawerOpen}
                aria-haspopup="dialog"
                onClick={() => setNoteDrawerOpen(true)}
              >
                Add note
              </Button>
              <ActionCard.PrimaryButton onClick={handleContinue}>Continue</ActionCard.PrimaryButton>
            </>
          )}
        </ActionCard.Footer>
        {details && (
          <ActionCard.Back
            title={details.title}
            description={
              details.subtitle ? (
                <QuestionSubtitleMarkdown markdown={details.subtitle} />
              ) : undefined
            }
          >
            <QuestionDetailsContent details={details} />
          </ActionCard.Back>
        )}
      </ActionCard>

      <Drawer open={noteDrawerOpen} onOpenChange={setNoteDrawerOpen}>
        <DrawerContent className={cn(BUDGET_CARD_PALETTE.cardBg, BUDGET_CARD_PALETTE.onCardFg)}>
          <DrawerHeader>
            <DrawerTitle>Add a note</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody className="flex min-h-0 flex-col gap-3">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note to include with your response"
              aria-label="Add a note to include with your response"
              className="min-h-31 resize-none border-withus-white/30 bg-white/5 p-4 text-withus-white placeholder:text-withus-white/40 focus-visible:border-withus-white focus-visible:ring-0"
            />
          </DrawerBody>
          <DrawerFooter>
            <Button
              variant="solid"
              color={BUDGET_CARD_PALETTE.primaryButtonColor}
              size="md"
              className={cn("w-full", BUDGET_CARD_PALETTE.primaryButtonClass)}
              onClick={() => {
                setNoteDrawerOpen(false);
                handleContinue();
              }}
            >
              Continue
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export function createMultipleChoiceQuestion(
  props: MultipleChoiceQuestionProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = props.questionId, canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <MultipleChoiceQuestion {...rest} {...core} />,
  };
}
